import {
	Chip,
	Divider,
	FormControl,
	Input,
	InputLabel,
	List,
	ListItem,
	MenuItem,
	Select,
	Typography,
} from '@material-ui/core';
import SaveModal from 'Components/Common/Modals/SaveModal';
import useAppSelector from 'Hooks/Redux/useAppSelector';
import useMessagesDispatch from 'Hooks/Redux/useMessagesDispatch';
import { UserClaimFlag, UserResponse } from 'Models/User';
import { ChangeEvent, useEffect, useState } from 'react';
import { MessageStatusLevel } from 'Redux/Actions/Messages';
import postUser, { PostUserRequest } from 'Utils/Http/Requests/Users/PostUserRequest';
import putUser from 'Utils/Http/Requests/Users/PutUserRequest';

interface CreateUserModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (isCreate: boolean, user: UserResponse) => void;
	user: UserResponse | null;
}

const CreateUserModal = (props: CreateUserModalProps) => {
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [passwordError, setPasswordError] = useState('');
	const [confirmPasswordError, setConfirmPasswordError] = useState('');
	const [claimFlag, setClaimFlag] = useState<number>(-1);

	const token = useAppSelector((x) => x.profile.token);
	const { dispatchSetAxiosErrorMessage, dispatchSetMessageAction } = useMessagesDispatch();
	const hasErrors = passwordError.length > 0 || confirmPasswordError.length > 0;
	const areEditFieldsSet = name.length > 0 && email.length > 0;
	const areCreateOnlyFieldsSet = password.length > 0 && confirmPassword.length > 0;
	const isFinished = areEditFieldsSet && (props.user !== null || areCreateOnlyFieldsSet) && !hasErrors;

	const claims = Object.entries(UserClaimFlag)
		.filter(([key]) => isNaN(Number(key)))
		.map(([key, value]) => ({ name: key, value: Number(value) }));

	const selectedClaims =
		claimFlag === -1
			? [-1]
			: claims.filter((claim) => (claimFlag & claim.value) === claim.value).map((claim) => claim.value);

	useEffect(() => {
		if (props.user === null) return;

		setName(props.user.name);
		setEmail(props.user.email);
		setClaimFlag(props.user.claimFlag);
	}, [props.user]);

	const handleClaimFlagChange = (event: ChangeEvent<{ value: unknown }>): void => {
		let values = event.target.value as number[];
		if (values.length === 0) setClaimFlag(-1);
		// In case all are removed we consider it has all claims.
		else if (values.length > 0) {
			values = values.filter((x) => x !== -1); // If at least one is added we consider it cannot have all claims anymore
			setClaimFlag(values.reduce((acc, curr) => acc | curr, 0));
		}
	};

	const handleNameInputChanged = (event: ChangeEvent<HTMLInputElement>): void => {
		setName(event.target.value);
	};

	const handleEmailInputChanged = (event: ChangeEvent<HTMLInputElement>): void => {
		setEmail(event.target.value);
	};

	const handlePasswordInputChanged = (event: ChangeEvent<HTMLInputElement>): void => {
		setPassword(event.target.value);
	};

	const handleConfirmPasswordChanged = (event: ChangeEvent<HTMLInputElement>): void => {
		setConfirmPassword(event.target.value);
	};

	const validatePassword = (): void => {
		if (password.length < 8) {
			setPasswordError('Password must be at least 8 characters long');
		} else {
			setPasswordError('');
		}
	};

	const validateConfirmPassword = (): void => {
		if (password !== confirmPassword) {
			setConfirmPasswordError('Passwords do not match');
		} else {
			setConfirmPasswordError('');
		}
	};

	const saveExistingUser = async (user: UserResponse): Promise<void> => {
		const u: Omit<UserResponse, 'organization'> = {
			id: user.id,
			name,
			email,
			claimFlag,
			enabled: true,
		};
		try {
			const response = await putUser(token, u, user.id);
			props.onSave(false, response.data.user);
			dispatchSetMessageAction(
				`Updated user ${response.data.user.name} with email ${response.data.user.email}`,
				MessageStatusLevel.Success
			);
		} catch (e) {
			dispatchSetAxiosErrorMessage(e);
		}
	};

	const saveNewUser = async (): Promise<void> => {
		const u: PostUserRequest = { name, email, password, claimFlag, enabled: true };
		try {
			const response = await postUser(token, u);
			props.onSave(true, response.data.user);
			dispatchSetMessageAction(
				`Created user ${response.data.user.name} with email ${response.data.user.email}`,
				MessageStatusLevel.Success
			);
		} catch (e) {
			dispatchSetAxiosErrorMessage(e);
		}
	};

	const handleSave = async (): Promise<void> => {
		if (props.user !== null) await saveExistingUser(props.user);
		else await saveNewUser();
		handleModalClosed();
	};

	const handleModalClosed = (): void => {
		setName('');
		setEmail('');
		setPassword('');
		setConfirmPassword('');
		setPasswordError('');
		setConfirmPasswordError('');
		props.onClose();
	};

	return (
		<SaveModal onSave={handleSave} open={props.isOpen} onClose={handleModalClosed} saveButtonDisabled={!isFinished}>
			<Typography variant="h5">Create User</Typography>
			<Divider />
			<List>
				<ListItem>
					<FormControl fullWidth={true}>
						<InputLabel required={true}>Name</InputLabel>
						<Input onChange={handleNameInputChanged} type="text" value={name} required={true} />
					</FormControl>
				</ListItem>
				<ListItem>
					<FormControl fullWidth={true}>
						<InputLabel required={true}>Email</InputLabel>
						<Input onChange={handleEmailInputChanged} type="email" value={email} required={true} />
					</FormControl>
				</ListItem>
				{props.user === null && (
					<>
						<ListItem>
							<FormControl fullWidth={true} error={passwordError.length > 0}>
								<InputLabel required={true}>Password</InputLabel>
								<Input
									onChange={handlePasswordInputChanged}
									type="password"
									value={password}
									required={true}
									onBlur={validatePassword}
									onFocus={() => setPasswordError('')}
								/>
								{passwordError && <Typography color="error">{passwordError}</Typography>}
							</FormControl>
						</ListItem>
						<ListItem>
							<FormControl fullWidth={true} error={confirmPasswordError.length > 0}>
								<InputLabel required={true}>Confirm Password</InputLabel>
								<Input
									onChange={handleConfirmPasswordChanged}
									type="password"
									value={confirmPassword}
									required={true}
									onBlur={validateConfirmPassword}
									onFocus={() => setConfirmPasswordError('')}
								/>
								{confirmPasswordError && <Typography color="error">{confirmPasswordError}</Typography>}
							</FormControl>
						</ListItem>
					</>
				)}
				<ListItem>
					<FormControl fullWidth={true}>
						<InputLabel>Claims</InputLabel>
						<Select
							multiple={true}
							value={selectedClaims}
							onChange={handleClaimFlagChange}
							renderValue={(selected) => (
								<div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
									{(selected as number[]).map((value) => {
										const label = value === -1 ? 'All' : claims.find((c) => c.value === value)?.name;
										return <Chip key={value} label={label} style={{ margin: 2 }} />;
									})}
								</div>
							)}
						>
							{claims.map((claim) => (
								<MenuItem key={claim.value} value={claim.value}>
									{claim.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</ListItem>
			</List>
		</SaveModal>
	);
};

export default CreateUserModal;
